import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, ImageBackground, ActivityIndicator, Animated, Platform, Easing, Image } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { venueListStyles } from '../styles/venueListStyles';
import { useVenues } from '../components/VenueContext';
import { performFuzzySearch, parseNaturalLanguageQuery } from '../utils/searchUtils';
import { venueMatchesFilters } from '../utils/venueFilters';
import { VenueReportForm } from '../components/VenueReportForm';
import { SearchBar } from '../components/SearchBar';
import { useAnimation } from '../hooks/useAnimation';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { Toast } from '../components/Toast';
import { Ionicons } from '@expo/vector-icons';
import { useUser } from '../components/LoginPersistence';
import { extractAddressFromMapEmbed, extractCoordinatesFromMapEmbed } from '../utils/mapEmbedUtils';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';

const UniqueVenueCard = ({ venue, onPress, index }) => {
  const fadeInRef = useRef(new Animated.Value(0));
  const slideUpRef = useRef(new Animated.Value(30));
  const { user } = useUser();
  const { theme, isDarkMode } = useTheme();
  const cardInfoColor = isDarkMode ? '#ffffff' : theme.text.primary;
  const cardCapacityColor = '#FFCB9C';

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeInRef.current, {
        toValue: 1,
        duration: 800,
        delay: index * 100,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: true
      }),
      Animated.timing(slideUpRef.current, {
        toValue: 0,
        duration: 800,
        delay: index * 100,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: true
      })
    ]).start();
  }, []);

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 2000) return '#97ff11'; // Perfect match - Green
    if (score >= 1500) return '#8BC34A'; // Very high - Light green
    if (score >= 1000) return '#CDDC39'; // High - Lime
    if (score >= 700) return '#FFEB3B';  // Medium - Yellow
    if (score >= 400) return '#FFC107';  // Low - Amber
    return '#FF5722';  // Very low - Deep Orange
  };

  // Format match types for display
  const formatMatchTypes = (types) => {
    if (!types) return '';
    return types.map(type => type.replace(/_/g, ' ')).join(', ');
  };

  // Extract address if not already present
  const venueWithAddress = venue.fullAddress ? venue : {
    ...venue,
    fullAddress: extractAddressFromMapEmbed(venue.mapEmbedLink)
  };

  // Check if venue has specific features
  const hasFeature = (venue, feature) => {
    switch (feature) {
      case 'backline':
        return venue.backline?.toLowerCase().includes('yes') || (venue.backline?.length > 0 && !venue.backline?.toLowerCase().includes('no'));
      case 'parking':
        return venue.parking?.toLowerCase().includes('yes');
      case 'rehearsal':
        return venue.backstage?.toLowerCase().includes('rehearsal');
      case 'pinball':
        return venue.backstage?.toLowerCase().includes('pinball') || venue.notes?.toLowerCase().includes('pinball');
      case 'merch100':
        return venue.merch?.toLowerCase().includes('100%');
      case 'stageFloor':
        return venue.stage?.toLowerCase().includes('floor');
      case 'shower':
        return venue.backstage?.toLowerCase().includes('shower');
      case 'laundry':
        return venue.backstage?.toLowerCase().includes('laundry');
      default:
        return false;
    }
  };

  return (
    <TouchableOpacity
      onPress={() => onPress(venueWithAddress)}
      activeOpacity={0.9}
    >
      <Animated.View
        style={[
          venueListStyles.venueCard,
          {
            opacity: fadeInRef.current,
            transform: [{ translateY: slideUpRef.current }],
            ...(Platform.OS === 'web' && {
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              ':hover': {
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
              }
            })
          }
        ]}
      >
        {venue.backgroundImage && (
          <ImageBackground
            source={{ uri: venue.backgroundImage }}
            style={venueListStyles.venueCardBackground}
            imageStyle={{ resizeMode: 'cover' }}
          />
        )}
        <View style={venueListStyles.venueCardContent}>
          <Text style={venueListStyles.venueCardName}>{venueWithAddress.name}</Text>
          <Text style={venueListStyles.venueCardAddress}>
            {venueWithAddress.city}, {venueWithAddress.state}
            {(venueWithAddress.distanceMiles !== undefined && venueWithAddress.distanceMiles != null && !isNaN(venueWithAddress.distanceMiles)) ? ` • ${venueWithAddress.distanceMiles.toFixed(1)} miles away` : ''}
          </Text>
          {venueWithAddress.fullAddress && (
            <Text style={venueListStyles.venueCardAddress}>
              {venueWithAddress.fullAddress}
            </Text>
          )}
          <View style={venueListStyles.venueCardDetails}>
            <Text style={[venueListStyles.venueCardInfo, { color: cardInfoColor }]}>
              {venueWithAddress.affiliate ? `${venueWithAddress.affiliate}` : ''}
              {venueWithAddress.affiliate && venueWithAddress.capacity ? ' • ' : ''}
              <Text style={[venueListStyles.venueCardCapacity, { color: cardCapacityColor }]}>
                {venueWithAddress.capacity ? `Capacity: ${venueWithAddress.capacity}` : ''}
              </Text>
            </Text>

            {/* Feature badges section */}
            <View style={venueListStyles.featureBadgesContainer}>
              {hasFeature(venueWithAddress, 'backline') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Backline</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'parking') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Parking</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'rehearsal') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Rehearsal</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'pinball') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Pinball!</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'merch100') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>100% Merch</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'stageFloor') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Floor Stage</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'shower') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Shower</Text>
                </View>
              )}
              {hasFeature(venueWithAddress, 'laundry') && (
                <View style={venueListStyles.featureBadge}>
                  <Text style={venueListStyles.featureBadgeText}>Laundry</Text>
                </View>
              )}
            </View>

            {/* Only show score debug info for admin users */}
            {user?.userType === 'admin' && venueWithAddress.score !== undefined && (
              <View style={venueListStyles.scoreDebugContainer}>
                <Text style={[
                  venueListStyles.scoreText,
                  { color: getScoreColor(venueWithAddress.score) }
                ]}>
                  Score: {venueWithAddress.score}
                </Text>
                {venueWithAddress.matchTypes && (
                  <Text style={venueListStyles.matchTypesText}>
                    {formatMatchTypes(venueWithAddress.matchTypes)}
                  </Text>
                )}
              </View>
            )}
          </View>
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
};


const FilterMenu = ({
  selectedFilters,
  onFilterChange,
  affiliateOptions,
  featureOptions,
  // subscriptionFeatureOptions=[],
  navigation,
  setToast,
  onClose,
}) => {
  const isDesktop = Platform.OS === 'web' && window.innerWidth > 768;
  const [openFilter, setOpenFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('Capacity');
  const { user } = useUser();
  const { theme, isDarkMode } = useTheme();
  // const isPremium = user?.subscriptionStatus === 'active' || (user?.subscriptionStatus === 'cancelled' && user?.subscriptionCancelAt && new Date(user?.subscriptionCancelAt) > new Date());
  const allChipOptions = [
    { label: 'ADA info',        value: 'hasADA',          type: 'feature' },
    { label: '100% merch',      value: 'merch100',        type: 'feature' },
    { label: 'indoor',          value: 'hasIndoor',       type: 'feature' },
    { label: 'backstage',       value: 'hasBackstage',    type: 'feature' },
    { label: 'rehearsal space', value: 'hasRehearsal',    type: 'feature' },
    { label: 'parking',         value: 'hasParking',      type: 'feature' },
    { label: 'outdoor',         value: 'hasOutdoorFloor', type: 'feature' },
    { label: 'backline',        value: 'hasBackline',     type: 'feature' },
    { label: 'lodging',         value: 'hasLodging',      type: 'feature' },
    { label: 'shore power',     value: 'hasShorePower',   type: 'feature' },
    { label: 'floorstage',      value: 'stageFloor',      type: 'feature' },
    { label: 'shower',          value: 'hasShower',       type: 'feature' },
    { label: 'pinball',         value: 'hasPinball',      type: 'feature' },
    { label: 'laundry',         value: 'hasLaundry',      type: 'feature' },
    { label: 'vegan',           value: 'hasVeganFood',    type: 'feature' },
    { label: 'DIY',             value: 'DIY',             type: 'affiliate' },
    { label: 'Independent',     value: 'Independent',     type: 'affiliate' },
    { label: 'Record Shop',     value: 'Record Shop',     type: 'affiliate' },
    { label: '',                value: 'Skate',           type: 'affiliate', image: isDarkMode ? require('../../assets/icons/skateboard_v2invert.png') : require('../../assets/icons/skateboard_v2.png') },
    { label: 'Art Gallery',     value: 'Art Gallery',     type: 'affiliate' },
    { label: 'Event Space',     value: 'Event Space',     type: 'affiliate' },
    { label: 'Nonprofit',       value: 'Nonprofit',       type: 'affiliate' },
    { label: 'Brewery',         value: 'Brewery',         type: 'affiliate' },
    { label: 'Coffee Shop',     value: 'Coffee Shop',     type: 'affiliate' },
    { label: 'Tea House',       value: 'Tea House',       type: 'affiliate' },
    { label: 'Restaurant',      value: 'Restaurant',      type: 'affiliate' },
  ];

  // Ensure features array exists
  const features = selectedFilters.features || [];

  // Update the Checkbox component: label wraps within fixed-width option (no truncation)
  const Checkbox = ({ checked, onPress, label, disabled, image }) => (
    <TouchableOpacity
      style={[
        venueListStyles.desktopOption,
        checked && venueListStyles.desktopOptionSelected,
        disabled && venueListStyles.desktopOptionDisabled
      ]}
      onPress={onPress}
      disabled={disabled}
    >
      <View style={[
        venueListStyles.checkbox,
        checked && venueListStyles.checkboxChecked,
        disabled && venueListStyles.checkboxDisabled
      ]}>
        {checked && <Text style={venueListStyles.checkmark}>✓</Text>}
      </View>
      {image && <Image alt={label} source={image} style={venueListStyles.checkboxImage} />}
      <View style={venueListStyles.desktopCheckboxLabelWrap}>
        <Text style={[
          venueListStyles.desktopCheckboxLabel,
          checked && venueListStyles.desktopOptionTextSelected
        ]}>{label}</Text>
      </View>
    </TouchableOpacity>
  );

  const MobileTabbedFilters = ({
    selectedFilters,
    onFilterChange,
    features,
    // isPremium, 
    navigation,
    setToast,
    affiliateOptions,
    featureOptions,
    // subscriptionFeatureOptions, 
    onClose,
    activeTab,
    setActiveTab
  }) => {


    // Determine mobile-friendly label
    const isMobile = Platform.OS !== 'web' || (typeof window !== 'undefined' && window.innerWidth <= 768);

    const tabs = [
      {
        id: 'Capacity',
        label: 'Capacity',
        isCapacity: true,
        isMulti: true,
      },
      {
        id: 'Filters',
        label: 'Filters',
        options: allChipOptions,
        selectedValue: [...features, ...selectedFilters.affiliate],
        isMulti: true,
        isCombined: true,
      },
      // {
      //   id: 'Premium',
      //   label: 'Subscription',
      //   options: subscriptionFeatureOptions,
      //   selectedValue: selectedFilters.subscriptionFeatures,
      //   isMulti: true
      // }
    ];

    const currentTab = tabs.find(tab => tab.id === activeTab);

    // Get all active filters for display
    const getActiveFilters = () => {
      const active = [];

      // Add capacity filters (min/max)
      if (selectedFilters.capacityMin || selectedFilters.capacityMax || selectedFilters.showUnknownCapacity) {
        let capacityDisplay = '';
        if (selectedFilters.capacityMin && selectedFilters.capacityMax) {
          capacityDisplay = `Capacity: ${selectedFilters.capacityMin}-${selectedFilters.capacityMax}`;
        } else if (selectedFilters.capacityMin) {
          capacityDisplay = `Capacity: ${selectedFilters.capacityMin}+`;
        } else if (selectedFilters.capacityMax) {
          capacityDisplay = `Capacity: ≤${selectedFilters.capacityMax}`;
        }

        if (selectedFilters.showUnknownCapacity) {
          capacityDisplay += selectedFilters.capacityMin || selectedFilters.capacityMax ? ' + Unknown' : 'Unknown Capacity';
        }

        if (capacityDisplay) {
          active.push({
            type: 'capacity',
            value: 'capacity',
            label: capacityDisplay,
            displayText: capacityDisplay
          });
        }
      }

      // Add affiliate filters (now supports multiple)
      if (selectedFilters.affiliate && selectedFilters.affiliate.length > 0) {
        selectedFilters.affiliate.forEach(affiliateValue => {
          const affiliateOption = affiliateOptions.find(opt => opt.value === affiliateValue);
          if (affiliateOption) {
            active.push({
              type: 'affiliate',
              value: affiliateValue,
              label: affiliateOption.label,
              displayText: `Affiliate: ${affiliateOption.label}`
            });
          }
        });
      }

      // Add feature filters
      if (features && features.length > 0) {
        features.forEach(featureValue => {
          const featureOption = featureOptions.find(opt => opt.value === featureValue);
          if (featureOption) {
            active.push({
              type: 'features',
              value: featureValue,
              label: featureOption.label,
              displayText: featureOption.label
            });
          }
        });
      }

      // Add subscription feature filters
      // if (selectedFilters.subscriptionFeatures && selectedFilters.subscriptionFeatures.length > 0) {
      //   selectedFilters.subscriptionFeatures.forEach(featureValue => {
      //     const featureOption = subscriptionFeatureOptions.find(opt => opt.value === featureValue);
      //     if (featureOption) {
      //       active.push({
      //         type: 'subscriptionFeatures',
      //         value: featureValue,
      //         label: featureOption.label,
      //         displayText: featureOption.label
      //       });
      //     }
      //   });
      // }

      return active;
    };

    const activeFilters = getActiveFilters();

    const removeFilter = (filterToRemove) => {
      if (filterToRemove.type === 'capacity') {
        onFilterChange('capacityMin', '');
        onFilterChange('capacityMax', '');
        onFilterChange('showUnknownCapacity', false);
      } else if (filterToRemove.type === 'affiliate') {
        const newAffiliates = selectedFilters.affiliate.filter(a => a !== filterToRemove.value);
        onFilterChange('affiliate', newAffiliates);
      } else if (filterToRemove.type === 'features') {
        const newFeatures = features.filter(f => f !== filterToRemove.value);
        onFilterChange('features', newFeatures);
      } else if (filterToRemove.type === 'subscriptionFeatures') {
        const newFeatures = selectedFilters.subscriptionFeatures.filter(f => f !== filterToRemove.value);
        onFilterChange('subscriptionFeatures', newFeatures);
      }
    };

    return (
      <View style={venueListStyles.mobileFilterTabs}>
        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <View style={venueListStyles.mobileActiveFilters}>
            <Text style={venueListStyles.activeFiltersTitle}>Active Filters:</Text>
            <View style={venueListStyles.activeFiltersContainer}>
              {activeFilters.map((filter, index) => (
                <TouchableOpacity
                  key={`${filter.type}-${filter.value}`}
                  style={venueListStyles.activeFilterChip}
                  onPress={() => removeFilter(filter)}
                >
                  <Text style={venueListStyles.activeFilterText}>{filter.displayText}</Text>
                  <Text style={venueListStyles.activeFilterRemove}>×</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* Tab Headers - Single Row */}
        <View style={venueListStyles.mobileTabHeaders}>
          {tabs.map(tab => (
            <TouchableOpacity
              key={tab.id}
              style={[
                venueListStyles.mobileTabHeader,
                activeTab === tab.id && venueListStyles.mobileTabHeaderActive
              ]}
              onPress={() => setActiveTab(tab.id)}
            >
              <Text style={[
                venueListStyles.mobileTabHeaderText,
                activeTab === tab.id && venueListStyles.mobileTabHeaderTextActive
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Tab Content */}
        <View style={venueListStyles.mobileTabContent}>
          {currentTab && currentTab.isCapacity ? (
            <View style={venueListStyles.mobileCapacityContent}>
              <View style={venueListStyles.mobileCapacityInputs}>
                <View style={venueListStyles.mobileCapacityRow}>
                  <TextInput
                    style={[venueListStyles.mobileCapacityInput, { color: theme.text.primary }]}
                    nativeID="mobile-capacity-min-input"
                    placeholder="Min"
                    placeholderTextColor={theme.text.muted}
                    value={String(selectedFilters.capacityMin || '')}
                    onChangeText={(text) => onFilterChange('capacityMin', text)}
                    keyboardType="numeric"
                  />
                  <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Text style={[venueListStyles.mobileCapacityToText, { color: theme.text.primary }]}>to</Text>
                  </View>
                  <TextInput
                    style={[venueListStyles.mobileCapacityInput, { color: theme.text.primary }]}
                    nativeID="mobile-capacity-max-input"
                    placeholder="Max"
                    placeholderTextColor={theme.text.muted}
                    value={String(selectedFilters.capacityMax || '')}
                    onChangeText={(text) => onFilterChange('capacityMax', text)}
                    keyboardType="numeric"
                  />
                </View>
                <TouchableOpacity
                  style={[venueListStyles.mobileTabOption, { width: '100%', justifyContent: 'flex-start' }]}
                  onPress={() => onFilterChange('showUnknownCapacity', !selectedFilters.showUnknownCapacity)}
                >
                  <Text style={[venueListStyles.filterChipText, selectedFilters.showUnknownCapacity && venueListStyles.filterChipTextActive]}>
                    show venues with unknown capacity
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View style={venueListStyles.mobileTabOptionsGrid}>
              {currentTab && currentTab.options.map((option, index) => {
                const isSelected = currentTab.isCombined
                  ? (option.type === 'feature' ? features.includes(option.value) : selectedFilters.affiliate.includes(option.value))
                  : currentTab.isMulti
                    ? currentTab.selectedValue.includes(option.value)
                    : currentTab.selectedValue === option.value;

                return (
                  <TouchableOpacity
                    key={option.value}
                    style={venueListStyles.mobileTabOption}
                    onPress={() => {
                      if (currentTab.isCombined) {
                        if (option.type === 'feature') {
                          const next = features.includes(option.value)
                            ? features.filter(f => f !== option.value)
                            : [...features, option.value];
                          onFilterChange('features', next);
                        } else {
                          const next = selectedFilters.affiliate.includes(option.value)
                            ? selectedFilters.affiliate.filter(a => a !== option.value)
                            : [...selectedFilters.affiliate, option.value];
                          onFilterChange('affiliate', next);
                        }
                      } else if (currentTab.isMulti) {
                        const newValue = currentTab.selectedValue.includes(option.value)
                          ? currentTab.selectedValue.filter(v => v !== option.value)
                          : [...currentTab.selectedValue, option.value];
                        onFilterChange(currentTab.id.toLowerCase(), newValue);
                      } else {
                        onFilterChange(currentTab.id.toLowerCase(), option.value);
                      }
                    }}
                  >
                    {option.image
                      ? <Image source={option.image} style={[venueListStyles.checkboxImage, { opacity: isSelected ? 1 : 0.4 }]} />
                      : <Text style={[venueListStyles.filterChipText, { color: isDarkMode ? theme.text.secondary : '#333'}, isSelected && venueListStyles.filterChipTextActive]} numberOfLines={2}>{option.label}</Text>

                    }
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
      </View>
    );
  }

  if (isDesktop) {
    return (
      <View style={venueListStyles.filtersContainer}>
        <View style={venueListStyles.filtersRow}>
          {/* Capacity */}
          <View style={venueListStyles.desktopFilterContainer}>
            <View style={venueListStyles.capacityInputsContainer}>
              <View style={venueListStyles.capacityInputRow}>
                <TextInput
                  style={[venueListStyles.capacityInput, { color: theme.text.primary }]}
                  nativeID="capacity-min-input"
                  placeholder="50"
                  placeholderTextColor={theme.text.muted}
                  value={selectedFilters.capacityMin}
                  onChangeText={(text) => onFilterChange('capacityMin', text)}
                  keyboardType="numeric"
                />
                <Text style={[venueListStyles.capacityToText, { color: theme.text.primary }]}>to</Text>
                <TextInput
                  style={[venueListStyles.capacityInput, { color: theme.text.primary }]}
                  nativeID="capacity-max-input"
                  placeholder="100"
                  placeholderTextColor={theme.text.muted}
                  value={selectedFilters.capacityMax}
                  onChangeText={(text) => onFilterChange('capacityMax', text)}
                  keyboardType="numeric"
                />
              </View>
              <TouchableOpacity onPress={() => onFilterChange('showUnknownCapacity', !selectedFilters.showUnknownCapacity)}>
                <Text style={[venueListStyles.filterChipText, selectedFilters.showUnknownCapacity && venueListStyles.filterChipTextActive, { color: theme.text.primary }]}>
                  show venues with unknown capacity
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* All chips — exact order; wraps to next row when row width < 800px */}
          <View style={{ flex: '1 1 0%', minWidth: 800 }}>
            <View style={venueListStyles.chipGrid4}>
              {allChipOptions.map(option => {
                const isActive = option.type === 'feature'
                  ? features.includes(option.value)
                  : selectedFilters.affiliate.includes(option.value);
                return (
                  <TouchableOpacity
                    key={option.value}
                    style={venueListStyles.chipCell4}
                    onPress={() => {
                      if (option.type === 'feature') {
                        const next = features.includes(option.value)
                          ? features.filter(f => f !== option.value)
                          : [...features, option.value];
                        onFilterChange('features', next);
                      } else {
                        const next = selectedFilters.affiliate.includes(option.value)
                          ? selectedFilters.affiliate.filter(a => a !== option.value)
                          : [...selectedFilters.affiliate, option.value];
                        onFilterChange('affiliate', next);
                      }
                    }}
                  >
                    {option.image
                      ? <Image source={option.image} style={[venueListStyles.checkboxImage, { opacity: isActive ? 1 : 0.4 }]} />
                      : <Text style={[venueListStyles.filterChipText, { color: isDarkMode ? theme.text.secondary : '#333'}, isActive && venueListStyles.filterChipTextActive]}>{option.label}</Text>
                    }
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <MobileTabbedFilters
      selectedFilters={selectedFilters}
      onFilterChange={onFilterChange}
      features={features}
      // isPremium={true}
      navigation={navigation}
      setToast={setToast}
      affiliateOptions={affiliateOptions}
      featureOptions={featureOptions}
      // subscriptionFeatureOptions={subscriptionFeatureOptions}
      onClose={onClose}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
}

export const VenueList = forwardRef(({ route }, ref) => {
  const { theme, isDarkMode } = useTheme();
  const navigation = useNavigation();
  const { venues, allVenues, loading: venuesLoading, loadingMore, hasMoreVenues, loadMoreVenues, filterVenuesByLocation } = useVenues();
  const { user } = useUser();
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [allFilteredVenues, setAllFilteredVenues] = useState([]); // Store all filtered results
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(route.params?.searchQuery || '');
  const [isSearching, setIsSearching] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const fadeInRef = useRef(new Animated.Value(0));
  const slideUpRef = useRef(new Animated.Value(30));
  const [filters, setFilters] = useState({
    affiliate: route.params?.affiliate ? (Array.isArray(route.params.affiliate) ? route.params.affiliate : [route.params.affiliate]) : [],
    capacityMin: '',
    capacityMax: '',
    showUnknownCapacity: false,
    features: Array.isArray(route.params?.features) ? route.params.features : [],
    subscriptionFeatures: []
  });
  const [openFilter, setOpenFilter] = useState(null);
  const [isFilterContainerVisible, setIsFilterContainerVisible] = useState(user?.hideFiltersVenueList ? false : true);
  const lastScrollY = useRef(0);
  const filterContainerHeight = useRef(new Animated.Value(user?.hideFiltersVenueList ? 0 : 1)).current;
  const [isMobile, setIsMobile] = useState(false);
  const [toast, setToast] = useState(null);

  // Remove the desktop-specific auto-show logic from the useEffect
  useEffect(() => {
    if (Platform.OS === 'web') {
      const checkIfMobile = () => {
        const mobile = window.innerWidth <= 768;
        setIsMobile(mobile);
      };

      checkIfMobile();
      window.addEventListener('resize', checkIfMobile);

      return () => {
        window.removeEventListener('resize', checkIfMobile);
      };
    }
  }, []);

  // Function to toggle filter container visibility
  const toggleFilterContainer = (visible) => {
    Animated.timing(filterContainerHeight, {
      toValue: visible ? 1 : 0,
      duration: 200,
      useNativeDriver: false
    }).start();
    setIsFilterContainerVisible(visible);
  };

  // Handle scroll events to show/hide filter container and load more venues
  const handleScrollInternal = (event) => {
    const currentScrollY = event.nativeEvent.contentOffset.y;

    if (isMobile) {
      // Always hide filters when scrolling on mobile
      // if (currentScrollY > 10 && isFilterContainerVisible) {
      //   toggleFilterContainer(false);
      // }

      // Close any open filters when scrolling
      if (openFilter !== null) {
        setOpenFilter(null);
      }
    }

    // Check if user has scrolled near the bottom to load more venues
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isNearBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;

    if (isNearBottom && filteredVenues.length < allFilteredVenues.length) {
      console.log('📄 Loading more filtered venues...', `${filteredVenues.length}/${allFilteredVenues.length}`);
      // Load more from filtered results
      const currentLength = filteredVenues.length;
      const nextBatch = allFilteredVenues.slice(currentLength, currentLength + 30);
      setFilteredVenues(prev => [...prev, ...nextBatch]);
    }

    lastScrollY.current = currentScrollY;
  };

  // Expose handleScrollInternal to parent component
  useImperativeHandle(ref, () => ({
    handleScrollInternal
  }));

  // Toggle mobile filter visibility button handler
  const handleToggleFiltersButton = () => {
    toggleFilterContainer(!isFilterContainerVisible);

    // If opening filters, scroll to top to give better context
    if (!isFilterContainerVisible && Platform.OS === 'web') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceKm = R * c;
    return {
      km: distanceKm,
      miles: distanceKm * 0.621371 // Convert to miles
    };
  };

  const handleSearch = () => {
    navigation.setParams({
      ...route.params,
      searchQuery: searchQuery
    });
  };

  const handleRemoveContext = () => {
    // Update navigation state with the modified params
    try {
      navigation.setParams({
        searchQuery: searchQuery,
        searchContext: null,
        sourceVenue: null
      });
    } catch (error) {
      console.error('Error removing context:', error);
    }

  }

  const handleRemoveLocation = () => {
    if (userLocation) {
      // Clear all location-related parameters
      const { searchQuery, searchContext, sourceVenue } = route.params || {};
      navigation.setParams({
        ...route.params,
        latitude: null,
        longitude: null,
        nearMe: false
      });
    }

  }

  // Add a new function to handle location search
  const handleLocationSearch = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();

          // // Format the location string properly
          // const city = data.city || data.locality || '';
          // const state = data.principalSubdivision || '';
          // const searchString = `${city}, ${state}`.trim();

          // setSearchQuery(searchString);

          navigation.setParams({
            ...route.params,
            searchQuery: searchQuery,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            nearMe: true
          });
        } catch (error) {
          console.error('Error getting location details:', error);
          alert('Error getting your location details. Please try again.');
        }
        setIsSearching(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Error accessing location. Please allow location access and try again.');
        setIsSearching(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  useEffect(() => {
    if (route.params?.latitude && route.params?.longitude && route.params?.nearMe) {
      setUserLocation({
        latitude: route.params.latitude,
        longitude: route.params.longitude
      });
    } else if (!route.params?.nearMe) {
      setUserLocation(null);
    }
    if (route.params?.searchQuery) {
      setSearchQuery(route.params.searchQuery);
    }
    if (route.params?.affiliate) {
      const affiliateValue = route.params.affiliate;
      const affiliateArray = Array.isArray(affiliateValue) ? affiliateValue : [affiliateValue];
      setFilters(prev => ({ ...prev, affiliate: affiliateArray }));
    }
  }, [route.params?.searchQuery, route.params?.nearMe, route.params?.affiliate]); // Add affiliate dependency

  // A new `timestamp` means a fresh search launched from another screen (search bar, affiliate or
  // feature tag on VenueScreen). navigate() replaces params when returning to this screen, so reset
  // the typed query and filters instead of carrying over the previous search.
  useEffect(() => {
    if (!route.params?.timestamp) return;
    const { searchQuery: paramQuery, affiliate, features } = route.params;
    setSearchQuery(paramQuery || '');
    setFilters({
      affiliate: affiliate ? (Array.isArray(affiliate) ? affiliate : [affiliate]) : [],
      capacityMin: '',
      capacityMax: '',
      showUnknownCapacity: false,
      features: Array.isArray(features) ? features : [],
      subscriptionFeatures: []
    });
  }, [route.params?.timestamp]);

  useEffect(() => {
    performSearch();
  }, [venues, allVenues, route.params?.searchQuery, route.params?.searchContext, userLocation, filters]);

  // Update the useEffect that processes addresses
  useEffect(() => {
    const venuesForProcessing = allVenues.length > 0 ? allVenues : venues;
    if (venuesForProcessing && venuesForProcessing.length > 0) {
      // Process all venues to extract addresses from map embed links
      const processedVenues = venuesForProcessing.map(venue => {
        if ((!venue.fullAddress || venue.fullAddress.trim() === '') && venue.mapEmbedLink) {
          return {
            ...venue,
            fullAddress: extractAddressFromMapEmbed(venue.mapEmbedLink)
          };
        }
        return venue;
      });

      // Update any venues that now have addresses
      if (JSON.stringify(processedVenues) !== JSON.stringify(venuesForProcessing)) {
        console.log('Updated venue addresses from map embed links');
      }
    }
  }, [venues, allVenues]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeInRef.current, {
        toValue: 1,
        duration: 1000,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: true
      }),
      Animated.timing(slideUpRef.current, {
        toValue: 0,
        duration: 1000,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: true
      })
    ]).start();
  }, []);

  // Affiliates kept separate for filter state (stored under selectedFilters.affiliate)
  const affiliateOptions = [
    { label: 'DIY', value: 'DIY' },
    { label: 'Independent', value: 'Independent' },
    { label: 'Record Shop', value: 'Record Shop' },
    { label: 'Art Gallery', value: 'Art Gallery' },
    { label: 'Event Space', value: 'Event Space' },
    { label: 'Nonprofit', value: 'Nonprofit' },
    { label: 'Brewery', value: 'Brewery' },
    { label: 'Coffee Shop', value: 'Coffee Shop' },
    { label: 'Tea House', value: 'Tea House' },
    { label: 'Restaurant', value: 'Restaurant' },
  ];

  const featureOptions = [
    { label: 'ADA info', value: 'hasADA' },
    { label: '100% merch', value: 'merch100' },
    { label: 'indoor', value: 'hasIndoor' },
    { label: 'backstage', value: 'hasBackstage' },
    { label: 'rehearsal space', value: 'hasRehearsal' },
    { label: 'parking', value: 'hasParking' },
    { label: 'outdoor', value: 'hasOutdoorFloor' },
    { label: 'backline', value: 'hasBackline' },
    { label: 'lodging', value: 'hasLodging' },
    { label: 'shore power', value: 'hasShorePower' },
    { label: 'floorstage', value: 'stageFloor' },
    { label: 'shower', value: 'hasShower' },
    { label: 'pinball', value: 'hasPinball' },
    { label: 'laundry', value: 'hasLaundry' },
    { label: 'vegan', value: 'hasVeganFood' },
  ];

  // allChipOptions is defined at module level above FilterMenu

  // const subscriptionFeatureOptions = [
  //   { label: 'Show Notes (local bookers, venue\'s genres, lighting patch list)', value: 'showNotes' },
  //   { label: 'Show only results w/ Venue\'s Spec Link', value: 'hasSpecLink', premiumOnly: true }
  // ];

  const handleFilterToggle = (filterName) => {
    // If the same filter is clicked, close it
    if (openFilter === filterName) {
      setOpenFilter(null);
    } else {
      // Otherwise, open the selected filter and close any other open one
      setOpenFilter(filterName);
    }

    // On mobile, ensure filter container is visible when opening a filter
    if (isMobile && !isFilterContainerVisible && filterName !== null) {
      toggleFilterContainer(true);
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web' && !isMobile) {
      const handleClickOutside = (event) => {
        const filterElements = document.querySelectorAll('[data-filter-container="true"]');
        let clickedInsideFilter = false;

        filterElements.forEach(element => {
          if (element.contains(event.target)) {
            clickedInsideFilter = true;
          }
        });

        if (!clickedInsideFilter && openFilter) {
          setOpenFilter(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openFilter, isMobile]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'features' && !Array.isArray(value)) {
      value = [];
    }
    if (filterType === 'affiliate' && !Array.isArray(value)) {
      value = [];
    }
    setFilters(prev => ({ ...prev, [filterType]: value }));
    // if (filterType !== 'features') {
    //   setOpenFilter(null);
    // }
  };

  const handleFeatureToggle = (feature) => {
    setFilters(prev => ({ ...prev, [feature]: !prev[feature] }));
    // Don't close the dropdown when selecting features
  };

  // Update clearFilters function
  const clearFilters = () => {
    setFilters({
      affiliate: [],
      capacityMin: '',
      capacityMax: '',
      showUnknownCapacity: false,
      features: [],
      subscriptionFeatures: []
    });
    setOpenFilter(null);
  };

  // Update hasActiveFilters to remove sortBy check
  const hasActiveFilters = () => {
    return (
      (filters.affiliate && filters.affiliate.length > 0) ||
      filters.capacityMin !== '' ||
      filters.capacityMax !== '' ||
      filters.showUnknownCapacity ||
      (filters.features && filters.features.length > 0) ||
      (filters.subscriptionFeatures && filters.subscriptionFeatures.length > 0)
    );
  };

  const applyFilters = (venues) => {
    // REQ-104: same matchers as the map (utils/venueFilters.js), 'list' profile keeps this screen's
    // OR-over-affiliate-tokens and strict Parking rules (scripts/test-venue-filters.cjs checks parity).
    let filtered = venues.filter((venue) => venueMatchesFilters(venue, filters, { profile: 'list' }));

    // Default sorting by score
    filtered.sort((a, b) => (b.score || 0) - (a.score || 0));

    return filtered;
  };

  /**
   * How closely a venue relates to the venue the search was launched from
   * (same city/state/affiliate, similar capacity and tech specs). The source venue itself sinks to the bottom.
   */
  const scoreAssociationToSource = (venue, sourceVenueData) => {
    let contextScore = 0;
    let contextMatchTypes = [];

    // Same affiliate gets a bonus
    if (venue.affiliate && sourceVenueData.affiliate &&
      String(venue.affiliate).toLowerCase() === String(sourceVenueData.affiliate).toLowerCase()) {
      contextScore += 400;
      contextMatchTypes.push('same_affiliate');
    }

    // Similar capacity gets a bonus
    if (venue.capacity && sourceVenueData.capacity) {
      const venueCap = parseInt(venue.capacity);
      const sourceCap = parseInt(sourceVenueData.capacity);
      if (!isNaN(venueCap) && !isNaN(sourceCap)) {
        const capRatio = Math.min(venueCap, sourceCap) / Math.max(venueCap, sourceCap);
        if (capRatio > 0.8) {
          contextScore += 300;
          contextMatchTypes.push('similar_capacity');
        } else if (capRatio > 0.6) {
          contextScore += 200;
          contextMatchTypes.push('related_capacity');
        }
      }
    }

    // Same city gets a bonus
    if (venue.city && sourceVenueData.city &&
      String(venue.city).toLowerCase() === String(sourceVenueData.city).toLowerCase()) {
      contextScore += 500;
      contextMatchTypes.push('same_city');
    }

    // Same state gets a bonus
    if (venue.state && sourceVenueData.state &&
      String(venue.state).toLowerCase() === String(sourceVenueData.state).toLowerCase()) {
      contextScore += 300;
      contextMatchTypes.push('same_state');
    }

    // Similar technical specs
    const techFields = [
      'stage', 'power', 'lightingControl', 'lightingFixtures',
      'video', 'foh', 'pa', 'monitors', 'backstage', 'backline',
      'merch', 'parking', 'shorePower'
    ];

    let techMatches = 0;
    techFields.forEach(field => {
      if (venue[field] && sourceVenueData[field] &&
        String(venue[field]).toLowerCase().includes(String(sourceVenueData[field]).toLowerCase())) {
        techMatches++;
        contextScore += 100;
      }
    });

    if (techMatches > 0) {
      contextMatchTypes.push(`similar_tech_specs_${techMatches}`);
    }

    // Don't include the source venue itself in results
    if (venue.id === sourceVenueData.id) {
      contextScore = -10000;
    }

    return { contextScore, contextMatchTypes };
  };

  const performSearch = async () => {
    // Use allVenues if available, fallback to venues
    const venuesForSearch = allVenues.length > 0 ? allVenues : venues;

    if (!venuesForSearch || venuesForSearch.length === 0) {
      console.log('No venues available');
      setFilteredVenues([]);
      setTotalFilteredCount(0);
      return;
    }

    setIsSearching(true);

    try {
      console.log('🔍 Searching with:', venuesForSearch.length, 'total venues');
      let filtered = [...venuesForSearch];
      const query = route.params?.searchQuery || searchQuery;
      const sourceVenue = route.params?.sourceVenue;
      const searchContext = route.params?.searchContext;

      console.log('Performing search with query:', query);
      console.log('Total venues before filtering:', filtered.length);

      // First, ensure all venues have addresses extracted from map embed links
      // This applies to ALL venues, regardless of search query
      filtered = filtered.map(venue => {
        if ((!venue.fullAddress || venue.fullAddress.trim() === '') && venue.mapEmbedLink) {
          const extractedAddress = extractAddressFromMapEmbed(venue.mapEmbedLink);
          if (extractedAddress) {
            return {
              ...venue,
              fullAddress: extractedAddress
            };
          }
        }
        return venue;
      });

      const sourceVenueData = sourceVenue ? venuesForSearch.find(v => v.id === sourceVenue) : null;

      if (query) {
        // If search context exists, add it to the query to enhance matching
        let enhancedQuery = query || '';
        // let enhancedQueryTerms = enhancedQuery.toLowerCase().trim().split(/\s+/);
        let enhancedQueryTerms = [enhancedQuery.toLowerCase().trim()];

        // Apply search context if available (from another venue)
        if (searchContext) {
          // Create a specialized filter for similar venues when coming from VenueScreen
          if (sourceVenue) {
            if (sourceVenueData) {
              // Add source venue context to score bonus for similar venues
              filtered = filtered.map(venue => ({
                ...venue,
                ...scoreAssociationToSource(venue, sourceVenueData)
              }));
            }
          }

          // Add affiliate to query if available in context
          if (searchContext.affiliate && !enhancedQuery.includes(searchContext.affiliate)) {
            enhancedQuery = `${enhancedQuery} ${searchContext.affiliate}`.trim();
          }

          // Add capacity to query if available
          if (searchContext.capacity && !enhancedQuery.includes(searchContext.capacity)) {
            enhancedQuery = `${enhancedQuery} ${searchContext.capacity}`.trim();
          }

          // Update query terms for scoring
          if (enhancedQuery !== query) {
            enhancedQueryTerms = enhancedQuery.toLowerCase().trim().split(/\s+/);
          }
        }

        const queryTerms = enhancedQueryTerms;
        const fullQuery = enhancedQuery.toLowerCase().trim();

        // Function to calculate string similarity (Levenshtein distance)
        const calculateSimilarity = (str1, str2) => {
          const track = Array(str2.length + 1).fill(null).map(() =>
            Array(str1.length + 1).fill(null));
          for (let i = 0; i <= str1.length; i += 1) {
            track[0][i] = i;
          }
          for (let j = 0; j <= str2.length; j += 1) {
            track[j][0] = j;
          }
          for (let j = 1; j <= str2.length; j += 1) {
            for (let i = 1; i <= str1.length; i += 1) {
              const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
              track[j][i] = Math.min(
                track[j][i - 1] + 1,
                track[j - 1][i] + 1,
                track[j - 1][i - 1] + indicator,
              );
            }
          }
          return 1 - (track[str2.length][str1.length] / Math.max(str1.length, str2.length));
        };

        // Find close matches for venue names
        const findCloseMatches = () => {
          return venuesForSearch.map(venue => {
            const venueName = venue.name.toLowerCase();
            const similarity = calculateSimilarity(venueName, fullQuery);
            const wordSimilarities = queryTerms.map(term => {
              const venueWords = venueName.split(/\s+/);
              return Math.max(...venueWords.map(word => calculateSimilarity(word, term)));
            });
            const avgWordSimilarity = wordSimilarities.reduce((a, b) => a + b, 0) / wordSimilarities.length;

            return {
              venue,
              exactMatch: venueName === fullQuery,
              similarity,
              avgWordSimilarity
            };
          });
        };

        const closeMatches = findCloseMatches();
        const exactMatch = closeMatches.find(m => m.exactMatch);

        let results;
        if (exactMatch) {
          // Full scoring so exact name (10k) beats starts-with (5k); fixed 2000 used to allow that inversion.
          const exactScored = calculateVenueScore(exactMatch.venue, queryTerms, fullQuery);
          const exactWithTag = {
            ...exactScored,
            matchTypes: [...new Set(['perfect_match', ...(exactScored.matchTypes || [])])],
          };
          results = [
            exactWithTag,
            ...venuesForSearch
              .filter(venue => venue.id !== exactMatch.venue.id)
              .map(venue => calculateVenueScore(venue, queryTerms, fullQuery)),
          ];
        } else {
          // Process all venues with fuzzy matching
          results = closeMatches
            .map(match => {
              const baseScore = calculateVenueScore(match.venue, queryTerms, fullQuery);

              // Add similarity bonus for close matches
              if (match.similarity > 0.8) {
                baseScore.score += 900;
                baseScore.matchTypes.push('high_similarity');
              } else if (match.similarity > 0.6) {
                baseScore.score += 700;
                baseScore.matchTypes.push('medium_similarity');
              }

              // Add word similarity bonus
              if (match.avgWordSimilarity > 0.8) {
                baseScore.score += 400;
                baseScore.matchTypes.push('word_similarity');
              }

              // Add distance-based scoring when user location is available
              if (userLocation && match.venue.mapEmbedLink && route.params?.nearMe) {
                const { lat, lng } = extractCoordinatesFromMapEmbed(match.venue.mapEmbedLink);

                const distance = calculateDistance(
                  userLocation.latitude,
                  userLocation.longitude,
                  parseFloat(lat),
                  parseFloat(lng)
                );

                // Add the distance to the venue object for display
                baseScore.distanceMiles = distance.miles;

                // Add distance-based scoring
                if (distance.miles <= 5) {
                  baseScore.score += 2000;
                  baseScore.matchTypes.push('very_close');
                } else if (distance.miles <= 10) {
                  baseScore.score += 1500;
                  baseScore.matchTypes.push('close');
                } else if (distance.miles <= 20) {
                  baseScore.score += 1000;
                  baseScore.matchTypes.push('nearby');
                } else if (distance.miles <= 30) {
                  baseScore.score += 500;
                  baseScore.matchTypes.push('within_30_miles');
                }
              }

              return baseScore;
            });
        }

        // Helper function to calculate venue score
        function calculateVenueScore(venue, queryTerms, fullQuery) {
          const venueCity = (venue.city || '').toLowerCase();
          const venueState = (venue.state || '').toLowerCase();
          const venueAffiliate = (venue.affiliate || '').toLowerCase();
          const venueName = (venue.name || '').toLowerCase();
          let venueFullAddress = venue.fullAddress;
          if (!venueFullAddress && venue.mapEmbedLink) {
            venueFullAddress = extractAddressFromMapEmbed(venue.mapEmbedLink).toLowerCase();
          } else {
            venueFullAddress = "";
          }

          // Additional venue data fields from CSV files
          const venueCapacity = (venue.capacity || '').toString().toLowerCase();
          const venueStage = (venue.stage || '').toLowerCase();
          const venuePower = (venue.power || '').toLowerCase();
          const venueLightingControl = (venue.lightingControl || '').toLowerCase();
          const venueLightingFixtures = (venue.lightingFixtures || '').toLowerCase();
          const venueVideo = (venue.video || '').toLowerCase();
          const venueFoh = (venue.foh || '').toLowerCase();
          const venuePa = (venue.pa || '').toLowerCase();
          const venueMonitors = (venue.monitors || '').toLowerCase();
          const venueBackstage = (venue.backstage || '').toLowerCase();
          const venueBackline = (venue.backline || '').toLowerCase();
          const venueMerch = (venue.merch || '').toLowerCase();
          const venueParking = (venue.parking || '').toLowerCase();
          const venueShorePower = (venue.shorePower || '').toLowerCase();
          const venueNotes = (venue.notes || '').toLowerCase();

          // Technical fields combined for broader matching
          const techSpecs = [
            venueStage, venuePower, venueLightingControl, venueLightingFixtures,
            venueVideo, venueFoh, venuePa, venueMonitors, venueBackstage,
            venueBackline, venueMerch, venueParking, venueShorePower
          ].join(' ').toLowerCase();

          let score = 0;
          let matchTypes = [];

          // Full address match
          if (venueFullAddress) {
            if (venueFullAddress === fullQuery) {
              score += 1000;
              matchTypes.push('exact_address_match');
            } else if (venueFullAddress.includes(fullQuery)) {
              score += 800;
              matchTypes.push('partial_address_match');
            }

            // Check for address components in query terms
            queryTerms.forEach(term => {
              if (venueFullAddress.includes(term)) {
                score += 300;
                matchTypes.push('address_term_match');
              }
            });

            // Check for consecutive terms in address
            const consecutiveAddressMatches = queryTerms.reduce((count, term, i) => {
              if (i < queryTerms.length - 1) {
                const twoTerms = `${term} ${queryTerms[i + 1]}`.toLowerCase();
                if (venueFullAddress.includes(twoTerms)) {
                  return count + 2;
                }
              }
              return count;
            }, 0);

            if (consecutiveAddressMatches > 0) {
              score += 400 + (consecutiveAddressMatches * 100);
              matchTypes.push('multi_word_address');
            }
          }

          // Full location match (city + state)
          const venueFullLocation = `${venueCity}, ${venueState}`.toLowerCase();
          const locationMatches = queryTerms.some(term => {
            const nextTermIndex = queryTerms.indexOf(term) + 1;
            if (nextTermIndex < queryTerms.length) {
              const twoTerms = `${term} ${queryTerms[nextTermIndex]}`.toLowerCase();
              return venueFullLocation.includes(twoTerms);
            }
            return false;
          });

          if (locationMatches) {
            score += 800;
            matchTypes.push('location_match');
          }

          // Multi-word city match
          const cityWords = venueCity.split(/\s+/);
          const consecutiveCityMatches = queryTerms.reduce((count, term, i) => {
            if (i < queryTerms.length - 1) {
              const twoTerms = `${term} ${queryTerms[i + 1]}`.toLowerCase();
              if (venueCity.includes(twoTerms)) {
                return count + 2;
              }
            }
            return count;
          }, 0);

          if (consecutiveCityMatches > 0) {
            score += 600 + (consecutiveCityMatches * 100);
            matchTypes.push('multi_word_city');
          }

          // Affiliate + Location combination
          const hasAffiliateMatch = queryTerms.some(term => venueAffiliate.includes(term));
          const hasCityMatch = queryTerms.some(term => venueCity.includes(term));
          const hasStateMatch = queryTerms.some(term => venueState.includes(term));

          if (hasAffiliateMatch && (hasCityMatch || hasStateMatch)) {
            score += 1000;
            matchTypes.push('affiliate_location_match');
          }

          // Smart name matching
          if (venueName === fullQuery) {
            score += 10000; // Massive bonus for exact match
            matchTypes.push('exact_name_match');
          } else if (venueName.startsWith(fullQuery)) {
            score += 5000; // High bonus for starts with match
            matchTypes.push('starts_with_match');
          } else {
            const nameWords = venueName.split(/\s+/);
            const queryWords = fullQuery.split(/\s+/);

            let consecutiveMatches = 0;
            let maxConsecutive = 0;

            queryWords.forEach((qWord, i) => {
              if (nameWords.some(nWord => nWord.includes(qWord))) {
                consecutiveMatches++;
                maxConsecutive = Math.max(maxConsecutive, consecutiveMatches);
              } else {
                consecutiveMatches = 0;
              }
            });

            // Make sure that the number of words matched is at least the number of words in the query
            if (maxConsecutive >= queryWords.length) {
              score += 600 + (maxConsecutive * 100);
              matchTypes.push('consecutive_words');
            } else if (venueName.includes(fullQuery)) {
              score += 800;
              matchTypes.push('partial_name');
            }
          }

          // Capacity matching
          if (venueCapacity) {
            const capacityMatches = queryTerms.some(term => {
              // Direct number match
              if (venueCapacity === term) {
                return true;
              }
              // Range match: "100-500", "under 100", "over 1000"
              if (term.includes('under') && term.match(/under\s*(\d+)/)) {
                const max = parseInt(term.match(/under\s*(\d+)/)[1]);
                const venueCapacityNum = parseInt(venueCapacity);
                return !isNaN(venueCapacityNum) && venueCapacityNum < max;
              }
              if (term.includes('over') && term.match(/over\s*(\d+)/)) {
                const min = parseInt(term.match(/over\s*(\d+)/)[1]);
                const venueCapacityNum = parseInt(venueCapacity);
                return !isNaN(venueCapacityNum) && venueCapacityNum > min;
              }
              // Check for ranges like 100-500
              if (term.match(/(\d+)-(\d+)/)) {
                const [min, max] = term.match(/(\d+)-(\d+)/).slice(1).map(Number);
                const venueCapacityNum = parseInt(venueCapacity);
                return !isNaN(venueCapacityNum) && venueCapacityNum >= min && venueCapacityNum <= max;
              }
              return false;
            });

            if (capacityMatches) {
              score += 500;
              matchTypes.push('capacity_match');
            }
          }

          // Technical specs matching
          const techSpecMatches = queryTerms.some(term => techSpecs.includes(term));
          if (techSpecMatches) {
            score += 400;
            matchTypes.push('tech_spec_match');
          }

          // Individual term matching with improved city/state handling
          queryTerms.forEach(term => {
            // Venue name matches
            if (venueName === term) {
              score += 500;
              matchTypes.push('term_exact_name');
            } else if (venueName.includes(term)) {
              score += 400;
              matchTypes.push('term_partial_name');
            }

            // City matches with higher scores
            if (venueCity === term) {
              score += 500;
              matchTypes.push('exact_city');
            } else if (venueCity.includes(term)) {
              score += 300;
              matchTypes.push('partial_city');
            }

            // State matches
            if (venueState === term) {
              score += 200;
              matchTypes.push('state');
            } else if (venueState.includes(term)) {
              score += 100;
              matchTypes.push('partial_state');
            }

            // Affiliate matches with higher scores
            if (venueAffiliate === term) {
              score += 400;
              matchTypes.push('exact_affiliate');
            } else if (venueAffiliate.includes(term)) {
              score += 200;
              matchTypes.push('partial_affiliate');
            }

            // Match specific technical fields
            const techFields = [
              { field: 'stage', value: venueStage, weight: 300 },
              { field: 'power', value: venuePower, weight: 300 },
              { field: 'lighting', value: venueLightingControl + ' ' + venueLightingFixtures, weight: 300 },
              { field: 'video', value: venueVideo, weight: 250 },
              { field: 'foh', value: venueFoh, weight: 300 },
              { field: 'pa', value: venuePa, weight: 350 },
              { field: 'monitors', value: venueMonitors, weight: 300 },
              { field: 'backstage', value: venueBackstage, weight: 250 },
              { field: 'backline', value: venueBackline, weight: 300 },
              { field: 'merch', value: venueMerch, weight: 250 },
              { field: 'parking', value: venueParking, weight: 250 },
              { field: 'shorePower', value: venueShorePower, weight: 250 },
              { field: 'notes', value: venueNotes, weight: 150 }
            ];

            techFields.forEach(({ field, value, weight }) => {
              if (value === term) {
                score += weight;
                matchTypes.push(`exact_${field}`);
              } else if (value.includes(term)) {
                score += weight * 0.6;
                matchTypes.push(`partial_${field}`);
              }
            });
          });

          return {
            ...venue,
            score,
            matchTypes: [...new Set(matchTypes)] // Deduplicate match types
          };
        }

        // Filter and sort the results
        filtered = results
          .filter(venue => venue.score > 0 || (venue.contextScore && venue.contextScore > 0))
          .map(venue => {
            // Combine searchScore and contextScore for final ranking
            const finalScore = (venue.score || 0) + (venue.contextScore || 0);

            // Combine match types
            const allMatchTypes = [
              ...(venue.matchTypes || []),
              ...(venue.contextMatchTypes || [])
            ];

            return {
              ...venue,
              score: finalScore,
              matchTypes: [...new Set(allMatchTypes)] // Deduplicate
            };
          })
          // No cap: the list pages in 30 at a time, and a cap of 60 hid most Live Nation / AEG venues (BUG-045).
          .sort((a, b) => b.score - a.score);

        console.log('Search results breakdown:', {
          query: fullQuery,
          totalMatches: filtered.length,
          topResults: filtered.slice(0, 3).map(v => ({
            name: v.name,
            score: v.score,
            matchTypes: v.matchTypes
          }))
        });

        // If no results found, try general fuzzy search
        if (filtered.length === 0) {
          console.log('No matches found, trying general fuzzy search');
          filtered = performFuzzySearch([...venues], query)
            .map(venue => ({ ...venue, score: 1 }))
            .slice(0, 60);
          console.log('Fuzzy search results:', filtered.length);
        }
      } else if (sourceVenueData) {
        // No query but launched from a venue (e.g. a feature tag): rank by association to that venue,
        // leaving the source venue out. score carries the ranking through applyFilters' sort.
        filtered = filtered
          .filter(venue => venue.id !== sourceVenueData.id)
          .map(venue => {
            const { contextScore, contextMatchTypes } = scoreAssociationToSource(venue, sourceVenueData);
            return { ...venue, contextScore, score: contextScore, matchTypes: contextMatchTypes };
          })
          .sort((a, b) => b.score - a.score || (a.name || '').localeCompare(b.name || ''));
      } else {
        // If no query, just filter by category/etc filters and sort by name
        filtered = filtered.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        // Ensure all venues have score and matchTypes for admin users
        if (user?.userType === 'admin') {
          filtered = filtered.map(venue => ({
            ...venue,
            score: venue.score || 1000,
            matchTypes: venue.matchTypes || ['no_search_query']
          }));
        }
      }

      // Apply filters after search
      filtered = applyFilters(filtered);

      console.log('Final filtered venues count:', filtered.length);

      // Store all filtered results for pagination
      setAllFilteredVenues(filtered);

      // Set total count for display
      setTotalFilteredCount(filtered.length);

      // Only show first 30 venues for initial display
      setFilteredVenues(filtered.slice(0, 30));
    } finally {
      setIsSearching(false);
    }
  };

  const handleVenueSelect = (venue) => {
    navigation.navigate('Venue', {
      venueId: venue.id,
      fromSubmitReport: route.params?.fromSubmitReport
    });
  };

  if (venuesLoading) {
    return (
      <View style={globalStyles.loadingContainer}>
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  return (
    <View style={[globalStyles.container, { opacity: fadeInRef.current }]}>
      <BackgroundVideo />
      <Animated.View
        style={[
          globalStyles.overlay,
          {
            opacity: fadeInRef.current,
            transform: [{ translateY: slideUpRef.current }],
            paddingTop: 0,
            // backgroundCoslor: '#f9f9f3',
          }
        ]}
      >
        <View style={venueListStyles.venueListContainer}>
          <View style={venueListStyles.searchWrapper}>
            <View style={[venueListStyles.searchContainer, { marginTop: 12 }]}>
              <SearchBar
                value={searchQuery}
                color={theme.text.primary}
                onChangeText={setSearchQuery}
                onSubmit={handleSearch}
                placeholder="Search by venue name, location, or affiliates..."
              />
              <TouchableOpacity
                style={venueListStyles.locationIconContainer}
                onPress={route.params?.nearMe && userLocation ? handleRemoveLocation : handleLocationSearch}
                disabled={isSearching}
              >
                {isSearching ? (
                  <ActivityIndicator size="small" color="#000000" />
                ) : (
                  <View style={venueListStyles.svgIcon}>
                    <View style={[venueListStyles.locationDot, userLocation && { backgroundColor: '#2ecc71' }]} />
                    <View style={[venueListStyles.locationRing, userLocation && { borderColor: '#2ecc71' }]} />
                  </View>
                )}
              </TouchableOpacity>
            </View>
          </View>

          {/* Filter buttons row */}
          <View style={venueListStyles.filterButtonsRow}>
            {/* Toggle Filters Button */}
            <TouchableOpacity
              style={[
                venueListStyles.filterToggleButton,
                hasActiveFilters() && venueListStyles.filterToggleButtonActive
              ]}
              onPress={handleToggleFiltersButton}
            >
              <Ionicons
                name={isFilterContainerVisible ? "close-outline" : "options-outline"}
                size={24}
                color="white"
                style={{ marginRight: 8 }}
              />
              <Text style={venueListStyles.filterToggleText}>
                {isFilterContainerVisible
                  ? 'Hide Filters'
                  : hasActiveFilters()
                    ? 'Filters (Active)'
                    : 'Show Filters'
                }
              </Text>
            </TouchableOpacity>

            {/* Search Context Button - only show when there is an active search context */}
            {route.params?.searchContext && route.params?.searchContext?.fromVenue && (
              <TouchableOpacity
                style={venueListStyles.contextActiveButton}
                onPress={handleRemoveContext}
              >
                <Text style={venueListStyles.contextActiveText}>
                  {route.params?.searchContext?.fromVenue ?
                    `Similar to: ${route.params.searchContext.fromVenue}` :
                    'Context Active'}
                </Text>
                <Ionicons
                  name="close-circle-outline"
                  size={20}
                  color="white"
                  style={{ marginLeft: 6 }}
                />
              </TouchableOpacity>
            )}

            {/* Clear Filters Button - only show when there are active filters */}
            {hasActiveFilters() && (
              <TouchableOpacity
                style={venueListStyles.clearFiltersButton}
                onPress={clearFilters}
              >
                <Ionicons
                  name="refresh-outline"
                  size={20}
                  color="black"
                  style={{ marginRight: 6 }}
                />
                <Text style={venueListStyles.clearFiltersText}>Clear Filters</Text>
              </TouchableOpacity>
            )}


          </View>

          {/* Venue Count Display */}
          {!isSearching && filteredVenues && (
            <View style={venueListStyles.venueCountContainer}>
              <Text style={[venueListStyles.venueCountText, { color: isDarkMode ? theme.text.secondary : '#333' }]}>
                {totalFilteredCount || filteredVenues.length} venue{(totalFilteredCount || filteredVenues.length) !== 1 ? 's' : ''} found
              </Text>
            </View>
          )}

          {/* Animated Filter Container */}
          <Animated.View
            style={[
              venueListStyles.filtersContainer,
              {
                maxHeight: filterContainerHeight.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 'auto']
                }),
                opacity: filterContainerHeight,
                overflow: 'hidden',
                zIndex: isMobile ? 1000 : 9999,
                position: 'relative',
                display: !isFilterContainerVisible ? 'none' : 'flex'
              }
            ]}
          >
            <FilterMenu
              selectedFilters={filters}
              onFilterChange={handleFilterChange}
              affiliateOptions={affiliateOptions}
              featureOptions={featureOptions}
              // subscriptionFeatureOptions={subscriptionFeatureOptions}
              navigation={navigation}
              setToast={setToast}
              onClose={() => toggleFilterContainer(false)}
            />
          </Animated.View>

          <View
            style={[
              venueListStyles.venueGrid,
              { zIndex: isMobile ? 10 : 1 }
            ]}
          >
            <View style={venueListStyles.resultsContainer}>
              {filteredVenues && filteredVenues.length > 0 ? (
                <>
                  {filteredVenues.map((venue, index) => (
                    <UniqueVenueCard
                      key={venue.id || index}
                      venue={venue}
                      index={index}
                      onPress={handleVenueSelect}
                    />
                  ))}
                  {filteredVenues.length < allFilteredVenues.length && (
                    <View style={{ padding: 20, alignItems: 'center' }}>
                      <ActivityIndicator size="large" color="#4e7de0" />
                      <Text style={{ color: 'black', marginTop: 10 }}>Loading more venues...</Text>
                    </View>
                  )}
                </>
              ) : (
                !isSearching && (
                  <Text style={[venueListStyles.venueCardInfo, { color: 'black', width: '100%', textAlign: 'center', marginTop: 20 }]}>
                    No venues found
                  </Text>
                )
              )}
            </View>
          </View>
        </View>
      </Animated.View>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onHide={() => setToast(null)}
        />
      )}
    </View>
  );
}); 